import * as React from 'react'
import { Analytics } from '@vercel/analytics/next'
import { GoogleTagManager } from '@next/third-parties/google'
import App from 'next/app'
import { useRouter } from 'next/router'
import { NextIntlClientProvider } from 'next-intl'
import { SWRConfig } from 'swr'
import { parseCookies } from 'nookies'
import Cookies from 'js-cookie'
import * as yup from 'yup'

import ruMessages from '../messages/ru.json'
import enMessages from '../messages/en.json'
import kkMessages from '../messages/kk.json'
import kyMessages from '../messages/ky.json'

import { withAuthContext } from 'lib/authContext'
import withResponsiveContext from 'lib/withResponsiveContext'
import withAnnouncements from 'lib/withAnnouncements'
import withLightBox from 'lib/withLightBox'
import withModal from 'lib/withModal'
import withModalStack from 'lib/withModalStack'
import withTooltip from 'lib/withTooltip'
import withCountry from 'lib/withCountry'
import withSearch from 'lib/searchContext'
import withWouter from 'lib/withWouter'

import DefaultLayout from 'components/DashboardLayout'
import { getAuthenticatedClient } from 'utils/api-client-wrapper'
import { transformOrderData, transformRecipientData } from 'utils/api-mappers'
import { compose } from 'utils/misc'
import { getDefaultSize } from 'utils/responsive'

import { Agentation } from 'agentation'

import 'utils/hamburgers.css'
import 'styles/tailwind.css'

function YupLocaleSync({ messages }: { messages: any }) {
  const prev = React.useRef<any>(null)
  if (messages?.yup && messages.yup !== prev.current) {
    prev.current = messages.yup
    yup.setLocale(messages.yup)
  }
  return null
}

import type { MessageKey } from '../types/i18n'

const MESSAGES_BY_LOCALE = {
  ru: ruMessages,
  en: enMessages,
  kk: kkMessages,
  ky: kyMessages,
} as const

type SupportedLocale = keyof typeof MESSAGES_BY_LOCALE

function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return (
    typeof locale === 'string' &&
    Object.prototype.hasOwnProperty.call(MESSAGES_BY_LOCALE, locale)
  )
}

function resolveLocale(locale: unknown): SupportedLocale {
  return isSupportedLocale(locale) ? locale : 'ru'
}

type Props = {
  Component: React.ComponentType<any> & {
    layout?: React.ComponentType<any>
    title?: MessageKey
  }
  pageProps: any
  messages: any

  country: string
  authToken: string | null
}

function ShipperApp(props: Props) {
  const { Component, pageProps, authToken, messages } = props
  const Layout = Component.layout || DefaultLayout
  const pageTitle = Component.title
  const router = useRouter()
  const activeLocale = resolveLocale(router.locale)
  const activeMessages = MESSAGES_BY_LOCALE[activeLocale] ?? messages
  const shouldLoadGtm =
    process.env.NODE_ENV === 'production' &&
    process.env.NEXT_PUBLIC_UP_STAGE !== 'test'

  return (
    <SWRConfig
      value={{
        fetcher: (url) => {
          const runtimeToken =
            typeof window !== 'undefined'
              ? Cookies.get('authToken') || authToken
              : authToken
          const client = getAuthenticatedClient(runtimeToken)
          return client
            .get(url, {
              headers: runtimeToken
                ? {
                    Authorization: runtimeToken,
                  }
                : undefined,
            })
            .then((res) => {
            const data = res.data

            // Apply transformations based on URL patterns
            if (url.includes('v1/orders')) {
              return transformOrderData(data)
            }
            if (url.includes('v1/recipients')) {
              return transformRecipientData(data)
            }

            // Return raw data for other endpoints (like cities, exchange rates, etc.)
            return data
          })
        },
      }}
    >
      <Layout title={pageTitle}>
        {shouldLoadGtm && <GoogleTagManager gtmId="GTM-5F9PGLJV" />}

        <Component authToken={authToken} {...pageProps} />

        <Analytics />

        {process.env.NODE_ENV === 'development' && <Agentation />}
      </Layout>
    </SWRConfig>
  )
}

ShipperApp.getInitialProps = async (appContext: any) => {
  const initialProps = await App.getInitialProps(appContext)

  const locale = resolveLocale(appContext.ctx?.locale || appContext.router?.locale)
  const messages = MESSAGES_BY_LOCALE[locale] ?? ruMessages

  if (appContext.ctx.req) {
    const mobileDetect = require('../utils/mobileDetect')
    ;(initialProps as any).defaultWindowSize = getDefaultSize(
      mobileDetect(appContext.ctx.req.headers),
    )

    const cookies = parseCookies(appContext.ctx)
    if (cookies && cookies.authToken) {
      ;(initialProps as any).authToken = cookies.authToken
    }
  } else if (typeof window !== 'undefined') {
    // Client-side navigation (e.g. locale change) — preserve auth token from cookie
    const authToken = Cookies.get('authToken')
    if (authToken) {
      ;(initialProps as any).authToken = authToken
    }
  }

  return { ...initialProps, messages }
}

const ComposedApp = compose(
  withWouter,
  withResponsiveContext,
  withAuthContext,
  withCountry,
  withAnnouncements,
  withLightBox,
  withModalStack,
  withModal,
  withTooltip,
  withSearch,
)(ShipperApp)

// Wrap the entire composed app with NextIntlClientProvider so that
// all HOC-rendered content (modals, tooltips, lightbox, etc.) has
// access to the intl context.
function AppWithIntl(props: Props) {
  const router = useRouter()
  const activeLocale = resolveLocale(router.locale)
  const activeMessages =
    MESSAGES_BY_LOCALE[activeLocale] ?? props.messages

  return (
    <NextIntlClientProvider
      key={activeLocale}
      locale={activeLocale}
      messages={activeMessages}
      timeZone="UTC"
    >
      <YupLocaleSync messages={activeMessages} />
      <ComposedApp {...props} />
    </NextIntlClientProvider>
  )
}

AppWithIntl.getInitialProps = ShipperApp.getInitialProps

export default AppWithIntl
